import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';

import BillingContext from '@totem/components/ticketing/billing/billingContext';
import {
  BilledEntry,
  BilledEntryItem,
} from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  billedEntry: BilledEntry;
};

const BillingBillingTable = ({ billedEntry }: Props) => {
  const { loading } = useContext(BillingContext);

  const columns: ColumnProps<BilledEntryItem>[] = [
    {
      title: 'Date',
      dataIndex: 'timeEntry.date',
      key: 'timeEntry.date',
      render: (_, record: BilledEntryItem) =>
        `${formatDateOnly(record.timeEntry.date)}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.timeEntry.date, compB.timeEntry.date),
    },

    {
      title: 'Hours',
      dataIndex: 'timeEntry.hoursLogged',
      key: 'timeEntry.hoursLogged',
      align: 'right',
      render: (_, record: BilledEntryItem) => (
        <Hours amount={record.timeEntry.hoursLogged} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.timeEntry.hoursLogged,
          compB.timeEntry.hoursLogged,
        ),
    },
    {
      title: 'Resource',
      dataIndex: 'resource.name',
      key: 'resource.name',
      render: (_, record: BilledEntryItem) => `${record.resource.name}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.resource.name, compB.resource.name),
    },
    {
      title: 'Description',
      dataIndex: 'timeEntry.description',
      key: 'timeEntry.description',
      render: (_, record: BilledEntryItem) => record.timeEntry.description,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.timeEntry.description,
          compB.timeEntry.description,
        ),
    },
    {
      title: 'Task',
      dataIndex: 'task.number',
      key: 'task.number',
      render: (_, record: BilledEntryItem) => {
        if (record.task !== null && record.task.name !== '') {
          return `${record.task.number}: ${record.task.name}`;
        }
        return '';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.task.number, compB.task.number),
    },
    {
      title: 'Ticket Number',
      dataIndex: 'ticket.number',
      key: 'ticket.number',
      render: (_, record: BilledEntryItem) => (
        <Link
          to={`/dashboard/ticket/${record.ticket.id}`}
          target={record.ticket.id}
        >
          {record.ticket.number}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.number, compB.ticket.number),
    },
    {
      title: 'Summary',
      dataIndex: 'ticket.summary',
      key: 'summary',
      render: (_, record: BilledEntryItem) => record.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.summary, compB.ticket.summary),
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building.id',
      render: (_, record: BilledEntryItem) => record.building.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(billedEntry) ? billedEntry.items : []}
      loading={loading}
      pagination={{
        showSizeChanger: true,
      }}
      rowKey={(record) => record.timeEntry.id}
    />
  );
};

export default BillingBillingTable;
